/// <amd-module name="VSS/ExtServices/LocationService" />
import { ServiceOptions, Services, TeamFoundationHostType, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { ILocationsRestClient } from "VSS/Platform/RestClient/Locations";
import { combineUrlPaths } from "VSS/Platform/Util/Url";

export const LocationServiceName = "ms.vss-features.location-service";

/**
 * Service for external content to get locations
 */
export interface ILocationService {
    /**
     * Gets the URL for the given REST resource area
     *
     * @param resourceAreaId Id of the resource area
     */
    getResourceAreaLocation(resourceAreaId: string): Promise<string>;

    /**
     * Gets the location of a remote service at a given host type.
     *
     * @param serviceInstanceType The GUID of the service instance type to lookup
     * @param hostType The host type to lookup (defaults to the host type of the current page data)
     */
    getServiceLocation(serviceInstanceType?: string, hostType?: TeamFoundationHostType): Promise<string>;

    /**
     * Attemps to create a url for the specified route template and paramaters.  The url will include host path.
     * For example, if the page url is https://dev.azure.com/foo and you try to create admin settings url for project "bar",
     * the output will be /foo/bar/_admin.
     *
     * This will asynchronously fetch a route contribution if it has not been included in page data.
     *
     * @param routeId Id of the route contribution
     * @param routeValues Route value replacements
     * @param hostPath Optional host path to use rather than the default host path for the page.
     */
    routeUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): Promise<string>;
}

function ensureAbsoluteUrl(url: string) {
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = combineUrlPaths(location.origin, url);
    }
    return url;
}

class LocationService extends VssService implements ILocationService {

    private _locationsByResource: { [resourceId: string]: Promise<string> } = {};

    public getResourceAreaLocation(resourceAreaId: string): Promise<string> {
        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        if (!pageData.isHosted) {
            // On-prem, use the current host location
            return Promise.resolve(ensureAbsoluteUrl(pageData.hostPath));
        }

        let locationPromise = this._locationsByResource[resourceAreaId];
        if (!locationPromise) {
            const locationsClient = this.pageContext.getRestClient<ILocationsRestClient>("ILocationsRestClient");
            locationPromise = locationsClient.getResourceArea(resourceAreaId).then(a => a.locationUrl);
            this._locationsByResource[resourceAreaId] = locationPromise;
        }

        return locationPromise;
    }

    public getServiceLocation(serviceInstanceType?: string, hostType?: TeamFoundationHostType): Promise<string> {
        const locationsClient = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        return locationsClient.getServiceLocationAsync(serviceInstanceType, hostType).then(ensureAbsoluteUrl);
    }

    public routeUrl(routeId: string, routeValues?: { [key: string]: string }, hostPath?: string): Promise<string> {
        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const routes = contributionService.getSharedData<{ [routeId: string]: string[] }>("_routes");
        if (routes && routes[routeId]) {
            return Promise.resolve(ensureAbsoluteUrl(locationService.routeUrl(routeId, routeValues, hostPath)));
        }

        const requestParams = {
            _sharedData: {
                routes: [routeId]
            }
        };
        return contributionService.getDataAsync("ms.vss-web.shared-data", requestParams).then(() => {
            return ensureAbsoluteUrl(locationService.routeUrl(routeId, routeValues, hostPath));
        });
    }
}

Services.add(LocationServiceName, { serviceFactory: LocationService, options: ServiceOptions.Remotable });
